import type {
	CustomQuestionnaireAnswersPayload,
	CustomQuestionnaireConfigurationDto,
} from '#api/customQuestionnaire'

import { useIntlConfig } from '@monthio/react-components'
import { captureException } from '@sentry/react'

import type { FormikValues, QuestionDefinition } from '../types'

import { validateAgainstQuestions } from '../validation'
import { convertToAnswerPayload, uploadDocuments } from './answers'
import { useQuestionnaireConfigurationDataModel } from './questionnaireConfig'

interface CustomFormModel {
	questions: {
		data: QuestionDefinition[] | undefined
	}
	submit?: (answers: FormikValues) => Promise<void>
	validate?: (answers: FormikValues) => Record<string, any>
	title?: string
}

export const useCustomFormModel = (
	languageCode: string,
	data: CustomQuestionnaireConfigurationDto,
	finish: (data: CustomQuestionnaireAnswersPayload) => Promise<void>,
): CustomFormModel => {
	const { currency } = useIntlConfig({ showCurrency: true })

	const questionnaireConfigModel = useQuestionnaireConfigurationDataModel(
		languageCode,
		data,
	)

	const questions = questionnaireConfigModel.data

	return {
		questions: questionnaireConfigModel,
		submit: questions
			? async (answers: FormikValues) => {
					if (!currency) {
						const error = new Error('Currency is not defined')
						captureException(error)

						throw error
					}

					const documentUploads = await uploadDocuments(answers, questions)

					const payload = convertToAnswerPayload(
						answers,
						questions,
						currency,
						data.versionId ?? 0,
						documentUploads,
					)

					await finish(payload)
				}
			: undefined,
		validate: questions
			? (answers) => validateAgainstQuestions(answers, questions)
			: undefined,
		title: questionnaireConfigModel.title,
	}
}
